/**
 * src/pages/AdminHelp.tsx
 * 管理者向け MangaOS 操作マニュアル
 *
 * 構成（4回に分けて拡張する前提）：
 *   Round 1（現在）: はじめに〜日付・月選択の考え方 + 各章プレースホルダー
 *   Round 2: アシスタント管理・シフト管理・日報管理
 *   Round 3: 給与管理・収入・固定費・月次レポート
 *   Round 4: 作品・話数・制作進捗・設定・FAQ
 */

import { useState } from 'react'

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------
type Section = {
  id: string
  title: string
  /** trueのとき「作成中」バッジを表示 */
  wip?: boolean
  content: React.ReactNode
}

// ----------------------------------------------------------------
// セクションデータ定義
// ----------------------------------------------------------------
const sections: Section[] = [
  // ============================================================
  // Round 1: 基礎セクション
  // ============================================================
  {
    id: 'intro',
    title: 'はじめに',
    content: (
      <>
        <p>このページは MangaOS の<strong>管理者向け操作マニュアル</strong>です。</p>
        <p>アシスタント管理・シフト・日報・給与・収支・作品進捗など、スタジオ運営に必要な機能の使い方をまとめています。</p>

        <div className="help-note">
          アシスタントさん向けのヘルプは別ページ（サイドバー下部「使い方・ヘルプ」）にあります。このページは管理者専用です。
        </div>

        <h4>マニュアルの使い方</h4>
        <ul>
          <li>左の目次から知りたい章を選んでください</li>
          <li>「🚧 準備中」と表示されている章は順次追加予定です</li>
          <li>操作で迷ったときはまず「画面共通の使い方」「データ登録・編集・削除の基本」を確認してください</li>
        </ul>
      </>
    ),
  },
  {
    id: 'overview',
    title: '管理画面でできること',
    content: (
      <>
        <p>MangaOS 管理画面では、スタジオ運営に関わる以下の業務を管理できます。</p>

        <table className="help-table">
          <thead>
            <tr><th>メニュー</th><th>主な機能</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>ダッシュボード</strong></td>
              <td>口座残高・KPI・引継ぎ事項・締切・日報・出勤・未払い支出・入金予定・カレンダーの一覧</td>
            </tr>
            <tr>
              <td><strong>作品・話数</strong></td>
              <td>話数の追加・工程進捗の更新・締切管理・制作計画・制作進行設定</td>
            </tr>
            <tr>
              <td><strong>アシスタント</strong></td>
              <td>勤怠入力・月次支払集計・シフト確定・給与申告確認・税務設定・アシスタント情報編集</td>
            </tr>
            <tr>
              <td><strong>支払管理</strong></td>
              <td>支出の追加・支払済管理・固定費テンプレートによる月次生成</td>
            </tr>
            <tr>
              <td><strong>収入・税積立</strong></td>
              <td>収入の登録・入金確認・税積立率の設定・月次収支の可視化</td>
            </tr>
            <tr>
              <td><strong>給与管理</strong></td>
              <td>給与明細の作成・確定・支払処理・税務設定・源泉所得税集計</td>
            </tr>
            <tr>
              <td><strong>月次レポート</strong></td>
              <td>月次の収支サマリー・人件費・支払・制作・シフト状況の確認と月次締め処理</td>
            </tr>
          </tbody>
        </table>

        <h4>管理者とアシスタントで共通のページ</h4>
        <p>「作品・話数」ページはアシスタントも閲覧できますが、管理者のみ話数の追加・工程更新・削除ができます。</p>
      </>
    ),
  },
  {
    id: 'common-ui',
    title: '画面共通の使い方',
    content: (
      <>
        <h4>ナビゲーション</h4>
        <ul>
          <li>左サイドバーのメニューから各ページへ移動します</li>
          <li>スマートフォンでは左上の「☰」（ハンバーガーメニュー）をタップしてメニューを開きます</li>
          <li>右上のメールアドレスをクリックするとアカウント設定へ移動します</li>
        </ul>

        <h4>テーマ・表示切り替え</h4>
        <p>右上のボタンから以下の切り替えができます。</p>
        <ul>
          <li><strong>Work モード / Manga モード</strong>：UIデザインの切り替え。どちらも機能は同じです</li>
          <li><strong>ライト / ダーク</strong>：明暗テーマの切り替え</li>
          <li><strong>ランナー表示 ON/OFF</strong>：画面下部のキャラクターアニメーションの表示切り替え</li>
        </ul>

        <h4>ステータスバッジの見方</h4>
        <p>一覧画面に表示されるバッジの色の意味は以下のとおりです。</p>
        <table className="help-table">
          <thead>
            <tr><th>色</th><th>意味の例</th></tr>
          </thead>
          <tbody>
            <tr><td>🟢 緑</td><td>完了・支払済・提出済・承認済</td></tr>
            <tr><td>🔵 青</td><td>確定・処理中</td></tr>
            <tr><td>🟡 黄・琥珀</td><td>下書き・要注意・期限が近い</td></tr>
            <tr><td>🔴 赤</td><td>未払い・エラー・期限超過・緊急</td></tr>
            <tr><td>⚪ グレー</td><td>未設定・非アクティブ・キャンセル</td></tr>
          </tbody>
        </table>

        <h4>アラートバナー</h4>
        <p>ダッシュボードや月次レポートなど複数の画面で、注意が必要な状態になるとページ上部にアラートバナーが表示されます。</p>
        <ul>
          <li><strong>赤（緊急）</strong>：締切超過・承認未処理など、すぐ対応が必要なもの</li>
          <li><strong>黄（注意）</strong>：期限が近い・申告未提出など、近日中に対応が必要なもの</li>
          <li><strong>青（情報）</strong>：確認事項・通知</li>
        </ul>

        <h4>モーダル（ポップアップ）の操作</h4>
        <ul>
          <li>入力後は「<strong>保存</strong>」または「<strong>確認</strong>」ボタンをクリックして確定します</li>
          <li>多くのモーダルは「<strong>✕</strong>」ボタンまたは背景（枠外）クリックで閉じます（未保存の内容は破棄されます）</li>
          <li>削除・締め処理など取り消しできない操作は、確認ダイアログが表示されます</li>
        </ul>
        <div className="help-note">
          誤操作で入力内容が消えるのを防ぐため、一部の入力モーダル（固定費テンプレートの新規作成・編集、支出追加、収入追加、勤怠入力など）は<strong>背景クリックでは閉じません</strong>。これらは右上の「✕」または「キャンセル」ボタンで閉じてください。保存に成功すると自動的に閉じます。
        </div>
      </>
    ),
  },
  {
    id: 'data-ops',
    title: 'データ登録・編集・削除の基本',
    content: (
      <>
        <h4>データを追加する</h4>
        <p>各ページの右上または上部にある「<strong>＋ ○○を追加</strong>」「<strong>＋ 新規作成</strong>」ボタンをクリックすると入力フォームが開きます。</p>
        <ol>
          <li>必須項目（<span style={{ color: 'var(--red)' }}>*</span> マーク付き）を入力する</li>
          <li>任意項目は必要に応じて入力する</li>
          <li>「<strong>保存</strong>」ボタンをクリックして確定する</li>
        </ol>
        <div className="help-note">
          保存ボタンを押す前にモーダルを閉じると、入力内容は破棄されます。
        </div>

        <h4>データを編集する</h4>
        <p>一覧の各行にある「<strong>編集</strong>」ボタン（または鉛筆アイコン）をクリックすると、編集フォームが開きます。修正後に「<strong>保存</strong>」をクリックしてください。</p>

        <div className="help-caution">
          ステータスが「支払済」「提出済」「締め済み」など確定した状態のデータは、直接編集できない場合があります。解除操作（「解除」ボタン）や管理者権限での操作が必要です。
        </div>

        <h4>データを削除する</h4>
        <p>「<strong>削除</strong>」ボタンをクリックすると確認ダイアログが表示されます。「削除する」をクリックすると削除が実行されます。</p>

        <div className="help-caution">
          削除したデータは基本的に復元できません。特に支払済・提出済のデータを削除する場合は慎重に操作してください。
        </div>

        <h4>一括操作</h4>
        <p>アシスタントページの月次支払など、複数のデータをまとめて処理できる場面では「<strong>一括支払済にする</strong>」「<strong>全員ステータス変更</strong>」などのボタンが表示されます。対象を確認してから実行してください。</p>

        <h4>CSVエクスポート</h4>
        <p>アシスタント管理・給与管理・月次レポートなどでは、データをCSV形式でダウンロードできます。</p>
        <table className="help-table">
          <thead>
            <tr><th>ボタン名</th><th>内容</th></tr>
          </thead>
          <tbody>
            <tr><td>支払集計CSV</td><td>支払金額の集計CSVファイル</td></tr>
            <tr><td>支払明細CSV</td><td>支払内訳の一覧CSV</td></tr>
            <tr><td>やよい仕訳CSV</td><td>弥生会計向けの仕訳データ</td></tr>
            <tr><td>やよい経費CSV</td><td>弥生会計向けの経費データ</td></tr>
            <tr><td>レポートCSV</td><td>月次レポートの集計データ</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'date-month',
    title: '日付・月選択の考え方',
    content: (
      <>
        <p>MangaOS の各ページでは「対象月」の概念が重要です。月を切り替えることで、その月のデータだけを表示・操作できます。</p>

        <h4>月選択の操作</h4>
        <ul>
          <li>「<strong>◀ ▶</strong>」ボタンで前月・翌月に移動できます</li>
          <li>月入力欄（例：<code>2026-06</code>）を直接クリックして変更もできます</li>
          <li>選択した月はブラウザに記憶され、次回アクセス時も同じ月が表示されます</li>
        </ul>

        <h4>「締め済み」月について</h4>
        <p>月次レポートで「<strong>承認・締め処理</strong>」を実行した月は「締め済み」になります。</p>
        <ul>
          <li>締め済み月は原則として編集・追加・削除ができなくなります</li>
          <li>間違いがあった場合は「<strong>締めを解除</strong>」ボタンで再オープンできます（解除理由の入力が必要）</li>
          <li>アシスタントからの給与申告・勤怠も、締め済み月は閲覧のみになります</li>
        </ul>

        <div className="help-note">
          月次締めは原則として月末の確認作業が終わった後に実行してください。締め後に修正が必要になると解除→再締めの手間が発生します。
        </div>

        <h4>「対象月」と「支払期日」の違い</h4>
        <p>支払管理や収入管理では「<strong>対象月</strong>」と「<strong>支払期日</strong>」が別々に管理されます。</p>
        <ul>
          <li><strong>対象月</strong>：費用・収入が発生した月（会計上の帰属月）</li>
          <li><strong>支払期日</strong>：実際に支払いが行われる日付</li>
        </ul>
        <p>例：5月分の家賃を6月末に支払う場合、対象月は「2026-05」、支払期日は「2026-06-30」となります。</p>

        <h4>シフト希望の締切について</h4>
        <p>アシスタントのシフト希望提出には「<strong>締切日</strong>」が設定できます。締切を過ぎると自動的に編集不可になり、確定シフトの作業に入れます。</p>
      </>
    ),
  },

  // ============================================================
  // Round 2: アシスタント管理・シフト管理・日報管理
  // ============================================================
  {
    id: 'personnel',
    title: 'アシスタント管理',
    content: (
      <>
        <p>「アシスタント」ページは、アシスタントさんの情報・勤怠・支払状況をまとめて管理するページです。ページ上部でタブを切り替えて各機能を使います。</p>

        <h4>タブ一覧</h4>
        <table className="help-table">
          <thead><tr><th>タブ名</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td><strong>メンバー概要</strong></td><td>全アシスタントの一覧・今月出勤日数・支払予定額・日給の確認</td></tr>
            <tr><td><strong>給与サマリー</strong></td><td>当月の勤怠明細（1件ずつ確認・修正・削除できる）</td></tr>
            <tr><td><strong>日報一覧</strong></td><td>全員の日報を一覧で確認（詳細クリックで全内容表示）</td></tr>
            <tr><td><strong>シフト希望</strong></td><td>アシスタントが提出したシフト希望の確認・未提出者への催促</td></tr>
            <tr><td><strong>シフト確定</strong></td><td>確定シフトの入力・確認メールの送信</td></tr>
            <tr><td><strong>支払準備</strong></td><td>月次支払集計・ステータス管理・振込CSVの作成</td></tr>
            <tr><td><strong>給与申告確認</strong></td><td>アシスタントが提出した給与申告の確認・確定額の入力</td></tr>
          </tbody>
        </table>

        <hr className="help-sep" />

        <h4>アシスタント情報を確認・編集する</h4>
        <p>「メンバー概要」タブで全アシスタントの一覧が確認できます。日給や今月の出勤状況も一覧に表示されます。</p>
        <p>情報を編集するには、右上の「<strong>人員管理 ▾</strong>」ボタンから「<strong>人員管理モードを開始</strong>」をクリックします。一覧の右端に「<strong>編集</strong>」ボタンが現れます。</p>

        <div className="help-note">
          人員管理モード中はバナーが表示されます。変更が終わったら「<strong>人員管理モードを終了</strong>」ボタンで通常表示に戻してください。
        </div>

        <p>編集モーダルで変更できる項目：</p>
        <ul>
          <li>名前・主担当（役割）・PC種別（Windows / Mac）</li>
          <li>対応可能作業（工程・得意度・優先順位・補正係数）</li>
          <li>日給（円）・交通費上限（円）</li>
          <li>メールアドレス</li>
        </ul>

        <div className="help-caution">
          <strong>メールアドレスについて：</strong><br />
          メールアドレスはログインIDを兼ねています。変更すると次回ログイン時から新しいアドレスでのログインが必要になります。<br />
          <code>@mangaos.test</code> で終わる仮メールアドレスはシステム管理用の仮IDです。実際にメールを受信できないため、確定シフト通知などのメール送信対象から除外されます。本人の実際のメールアドレスが確定したら更新してください。
        </div>

        <hr className="help-sep" />

        <h4>新しいアシスタントを追加する</h4>
        <p>「人員管理 ▾」→「<strong>＋ 新しいアシスタントを追加</strong>」からアシスタントを新規登録できます。ウィザード形式で必要な情報を順番に入力します。</p>

        <hr className="help-sep" />

        <h4>勤怠入力・修正</h4>
        <p>ページ右上の「<strong>+ 勤怠入力</strong>」ボタンから管理者が代理で勤怠を記録できます。アシスタントが記録を忘れた場合や、修正依頼が来た場合に使います。</p>

        <p>入力フォームの項目：</p>
        <ul>
          <li><strong>アシスタント</strong>：対象者を選択</li>
          <li><strong>日付</strong>：勤務日</li>
          <li><strong>勤務区分</strong>：フルタイム（日給）/ 時間勤務・時短勤務</li>
          <li><strong>出勤・退勤時刻・休憩時間</strong>：入力すると残業時間／勤務時間がその場で自動計算されます（計算ボタンはありません）</li>
          <li><strong>残業（分）</strong>：フルタイムの場合の自動計算結果（読み取り専用）</li>
          <li><strong>交通費（円）</strong>：当日の実費。アシスタントマスタの上限額を超えた分は振込に反映されません</li>
          <li><strong>食事支給</strong>：会社が直接負担するため振込額には含まれません（内部管理用）</li>
        </ul>

        <div className="help-note">
          残業時間は「勤務区分＝フルタイム（日給）」のときだけ自動計算されます（実働時間が標準勤務510分／8.5時間を超えた分）。「時間勤務・時短勤務」では残業は計上されず、出退勤時刻と休憩時間から勤務時間（分）だけが自動計算されます。保存時はサーバー側でも同じ式で必ず再計算されるため、手入力した残業値がそのまま使われることはありません。修正する場合は出勤・退勤・休憩時間を正しく直せば、残業・勤務時間は自動的に再計算されます。
        </div>

        <p>既存の勤怠を修正するには、「<strong>給与サマリー</strong>」タブを開き、対象レコードの「<strong>修正</strong>」ボタンをクリックします。削除は「<strong>削除</strong>」ボタンから行えます。</p>

        <div className="help-caution">
          月次締め済みの月は勤怠の新規登録・修正ができません。修正が必要な場合は月次レポートページで「締めを解除」してから対応してください。
        </div>

        <hr className="help-sep" />

        <h4>月次支払集計（支払準備タブ）</h4>
        <p>「<strong>支払準備</strong>」タブでは、当月の支払レコードを作成し、ステータスを管理します。月末〜支払日前に以下の手順で操作します。</p>

        <ol>
          <li>「<strong>勤怠から再集計</strong>」ボタンで勤怠データをもとに支払レコードを生成する</li>
          <li>一覧で金額を確認し、問題がなければ各行の「<strong>レビュー済</strong>」ボタンをクリック（または「<strong>未確認〇件をレビュー済にする</strong>」で一括処理）</li>
          <li>全員レビュー済になったら「<strong>承認・締め処理</strong>」で月を締める</li>
          <li>振込後に各行の「<strong>支払済</strong>」ボタンをクリック（または「<strong>承認済〇名を一括支払済</strong>」で一括処理）</li>
        </ol>

        <p>ステータスの意味：</p>
        <table className="help-table">
          <thead><tr><th>ステータス</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td><span className="badge badge-gray">未確認</span></td><td>集計直後。まだ内容を確認していない</td></tr>
            <tr><td><span style={{ fontSize: 12, background: 'var(--blue-light)', color: 'var(--blue)', border: '1px solid rgba(30,95,168,0.2)', borderRadius: 4, padding: '1px 7px' }}>レビュー済</span></td><td>内容を確認済み。承認待ち</td></tr>
            <tr><td><span className="badge badge-blue">承認済</span></td><td>承認・締め処理が完了。振込待ち</td></tr>
            <tr><td><span className="badge badge-green">支払済</span></td><td>振込完了</td></tr>
          </tbody>
        </table>

        <h4>申告確定額を反映して再集計</h4>
        <p>給与申告確認タブで「確定」済みのアシスタントがいる月は、「<strong>申告確定額を反映して再集計</strong>」ボタンを使うと、通常の勤怠集計の代わりに、申告で確定した金額をその人の支払レコードに反映できます。</p>
        <ul>
          <li>確定申告がないアシスタントは、通常どおり勤怠データをもとに集計されます</li>
          <li>一度この操作で申告確定額を反映した人は、その後「<strong>勤怠から再集計</strong>」を押しても<strong>上書きされません</strong>（自動的にスキップされます）</li>
          <li>「勤怠から再集計」を実行すると、スキップされた人数と対象者名がトースト通知に表示されます（例：「更新：3名 / スキップ：1名（申告確定額を反映済みのため：〇〇）」）</li>
        </ul>
        <div className="help-note">
          申告確定額を反映した集計を勤怠ベースに戻したい場合は、通常の削除・再生成の流れで対応してください（申告確定額の反映は「上書きしない」だけで、自動的に解除される仕組みではありません）。
        </div>

        <h4>支払CSVのダウンロード</h4>
        <p>支払準備タブの右上にダウンロードボタンがあります。</p>
        <table className="help-table">
          <thead><tr><th>ボタン名</th><th>用途</th></tr></thead>
          <tbody>
            <tr><td>支払集計CSV</td><td>支払金額のシンプルな集計CSV</td></tr>
            <tr><td>支払明細CSV</td><td>支払内訳の詳細一覧</td></tr>
            <tr><td>やよい仕訳CSV</td><td>弥生会計向けの仕訳データ</td></tr>
            <tr><td>やよい経費CSV</td><td>弥生会計向けの経費データ</td></tr>
          </tbody>
        </table>

        <p>※ MangaOSではアシスタントの振込先口座情報は保持しません（銀行振込は別途ご自身で行ってください）。</p>

        <hr className="help-sep" />

        <h4>給与申告確認タブ</h4>
        <p>アシスタントが「給与申告」ページから提出した申告内容を、管理者側で確認・確定するためのタブです。</p>

        <p>申告のステータス：</p>
        <table className="help-table">
          <thead><tr><th>ステータス</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>提出済み</td><td>アシスタントが申告を提出した状態</td></tr>
            <tr><td>レビュー済み</td><td>管理者が内容を確認した状態</td></tr>
            <tr><td>確定</td><td>管理者が確定額を入力して確定した状態。支払準備の「申告確定額を反映して再集計」で使われる</td></tr>
          </tbody>
        </table>

        <h4>提出忘れのリマインドメール</h4>
        <p>対象月の給与申告がまだ提出されていない（下書きのまま、または未着手の）アシスタントに、リマインドメールを送信できます。</p>
        <ul>
          <li>送信前に対象者のプレビュー（送信対象一覧）を確認できます</li>
          <li>送信後、成功・失敗の人数と、一人ずつの結果（✓ 送信済 / ✗ 失敗理由）が表示されます</li>
          <li>メール未登録のアシスタントには「メール未登録」、仮メール（<code>@mangaos.test</code>）のアシスタントには「仮メール」のバッジが付き、その人には送信されません</li>
        </ul>
        <div className="help-note">
          このリマインドメールの送信履歴は、確定シフトメールのような「送信済み」バッジとして常時表示されるものではありません。誰に送信済みかを確認したい場合は、送信直後の結果画面で確認してください。
        </div>

        <hr className="help-sep" />

        <h4>退職管理（人員管理モード中のみ操作可）</h4>
        <p>退職管理は人員管理モード中のみ操作できます。通常時は触りません。</p>

        <table className="help-table">
          <thead><tr><th>操作</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>退職予定にする</td><td>ログイン・日報・シフト対象から外す。最終給与のため給与対象には残せる</td></tr>
            <tr><td>退職済みにする</td><td>給与対象からも外す。最終精算が完了してから実行する</td></tr>
            <tr><td>現役に戻す</td><td>誤って退職処理した場合に元に戻す</td></tr>
          </tbody>
        </table>

        <div className="help-caution">
          退職処理を実行しても、過去の給与・日報・シフト履歴は削除されません。各タブから引き続き閲覧できます。最終給与・交通費の精算が完了してから「退職済みにする」を実行してください。
        </div>
      </>
    ),
  },
  {
    id: 'shift',
    title: 'シフト管理',
    content: (
      <>
        <p>シフト管理は「アシスタント」ページの「<strong>シフト希望</strong>」タブと「<strong>シフト確定</strong>」タブで行います。毎月の流れは「希望確認 → 調整 → 確定入力 → メール送信 → 変更時対応」です。</p>

        <h4>シフト希望を確認する（シフト希望タブ）</h4>
        <p>アシスタントが提出したシフト希望の一覧が表示されます。各行で確認できる情報：</p>
        <ul>
          <li><strong>出勤日数</strong>：希望として選んだ日数</li>
          <li><strong>出勤できる日</strong>：希望日の日付一覧</li>
          <li><strong>備考</strong>：アシスタントが入力したメモ</li>
          <li><strong>状態</strong>：「未確定」または「確定」</li>
          <li><strong>提出日時 / 更新日時</strong>：提出・更新のタイムスタンプ</li>
        </ul>

        <div className="help-note">
          一覧上部に3つの指標が表示されます。「提出済み ○/○名」「未提出 ○名」「締切日」を確認して、提出漏れがないかチェックしてください。
        </div>

        <h4>26日以降の更新に注意する</h4>
        <p>アシスタントが26日以降にシフト希望を更新した場合、「<strong>要確認</strong>」バッジが表示されます。タブ名にも件数バッジが付きます。確認が終わったら該当行の「<strong>確認済み</strong>」ボタンをクリックしてバッジを消してください。</p>

        <h4>未提出者に催促する</h4>
        <p>未提出者がいる場合、ページ上部に黄色（または赤色）のバナーが表示されます。「<strong>設定・通知</strong>」ボタンをクリックすると：</p>
        <ul>
          <li><strong>締切日</strong>の設定（日付を入力して「保存」）</li>
          <li><strong>催促メール</strong>の送信（任意のメッセージを入力して「○名に通知」）</li>
        </ul>

        <div className="help-caution">
          <code>@mangaos.test</code> の仮メールアドレスが設定されているアシスタントには実際にはメールが届きません。確認が必要な場合は直接連絡してください。
        </div>

        <hr className="help-sep" />

        <h4>確定シフトを作成する（シフト確定タブ）</h4>
        <p>「<strong>シフト確定</strong>」タブでは、希望をもとに各アシスタントの確定出勤日をクリックで入力します。</p>

        <p>表示モードは「<strong>日付チップ</strong>」と「<strong>週表示</strong>」の2種類で切り替えられます。どちらもクリックで確定/解除できます。</p>

        <table className="help-table">
          <thead><tr><th>色・記号</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>🟢 緑（✓）</td><td>確定済み（希望日と一致）</td></tr>
            <tr><td>🟡 黄</td><td>希望が出ているが未確定</td></tr>
            <tr><td>⚪ グレー</td><td>希望なし・未設定</td></tr>
          </tbody>
        </table>

        <p>主なボタン：</p>
        <ul>
          <li><strong>希望日を全員にコピー</strong>：提出済みの希望日をそのまま確定にコピーする（調整の起点として便利）</li>
          <li><strong>全員を保存（○名）</strong>：変更を一括保存。未保存の変更がある行はオレンジ色で表示される</li>
          <li><strong>↩ 変更を取り消す</strong>：保存前であれば直前の状態に戻せる</li>
        </ul>

        <div className="help-note">
          ページ上部に「予定人足数」と「予定人件費」の集計が表示されます。人件費が赤く表示される場合は上限（¥450,000）を超えています。シフト調整の参考にしてください。
        </div>

        <h4>PC設備上限の警告</h4>
        <p>同日にWindows 3名超または Mac 4名超が確定した場合、該当日に「⚠」マークが表示されます。このまま保存しようとすると確認ダイアログが出ます。設備上限は確認用の警告であり、強制停止ではありません。</p>

        <hr className="help-sep" />

        <h4>確定シフトをメールで送信する</h4>
        <p>「<strong>✉ 確定シフトをメール送信</strong>」ボタンをクリックすると、送信対象の選択モーダルが開きます。</p>

        <ol>
          <li>送信したいアシスタントにチェックを入れる（「<strong>変更があった人だけ選択</strong>」ボタンで差分のある人だけ選択できる）</li>
          <li>「<strong>選択した○名にメール送信</strong>」をクリック</li>
          <li>確認画面で内容を確認し「<strong>○名に送信する</strong>」をクリック</li>
          <li>送信結果が表示される</li>
        </ol>

        <div className="help-caution">
          「<strong>仮メール</strong>」バッジが付いているアシスタント（<code>@mangaos.test</code>）は実際にはメールが届きません。送信確認画面に警告が表示されます。送信後、該当アシスタントには別途連絡してください。
        </div>

        <div className="help-note">
          メール未登録のアシスタントはボタンに「○名未登録」と表示され、送信対象には選択できません。事前にメールアドレスを登録してください。
        </div>

        <hr className="help-sep" />

        <h4>確定シフトを変更したとき</h4>
        <p>確定後にシフトを変更した場合は、変更を保存してから「✉ 確定シフトをメール送信」で再送信します。送信モーダルの「変更があった人だけ選択」ボタンを使うと、前回送信後に変更があったアシスタントだけを自動で選択できます。</p>

        <div className="help-caution">
          全員に再送信すると「変更なし」の人にも通知が届きます。変更した人だけを選んで送信することを推奨します。
        </div>

        <hr className="help-sep" />

        <h4>アシスタント側からの見え方</h4>
        <p>アシスタントは「シフト」ページで自分の確定シフトを確認できます。管理者が確定シフトを保存した時点で反映されます。メール送信は確定シフトの通知手段であり、未送信でも確定内容はアシスタントが自分でページを開けば確認できます。</p>
      </>
    ),
  },
  {
    id: 'reports',
    title: '日報管理',
    content: (
      <>
        <p>管理者は全アシスタントの日報を「アシスタント」ページの「<strong>日報一覧</strong>」タブで確認できます。誰が・いつ・どの作業をしたかを把握し、進捗確認・引継ぎ・給与の根拠確認に役立ててください。</p>

        <h4>日報一覧の見方</h4>
        <p>「<strong>日報一覧</strong>」タブを開くと、当月の全アシスタントの日報が一覧表示されます。</p>

        <p>列の意味：</p>
        <table className="help-table">
          <thead><tr><th>列名</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td>日付</td><td>日報の日付</td></tr>
            <tr><td>担当者</td><td>提出したアシスタントの名前</td></tr>
            <tr><td>作品名</td><td>主な作業作品（複数作業がある場合は「他○件」と表示）</td></tr>
            <tr><td>話数</td><td>対象の話数番号</td></tr>
            <tr><td>工程</td><td>実施した工程（ネーム / 下書き / ペン入れ / 背景 / 仕上げ / 修正 / その他）</td></tr>
            <tr><td>合計P</td><td>完了ページ数の合計</td></tr>
            <tr><td>引継ぎ</td><td>引継ぎ・申し送りメモ（「特になし」の場合は薄く表示）</td></tr>
            <tr><td>画像</td><td>添付画像の枚数（📷マーク）</td></tr>
            <tr><td>提出日時</td><td>日報が提出された日時</td></tr>
          </tbody>
        </table>

        <p>各列ヘッダーをクリックすると、その列で並び替えができます。</p>

        <h4>日報の詳細を見る</h4>
        <p>一覧の行をクリックすると、日報の詳細モーダルが開きます。確認できる内容：</p>
        <ul>
          <li>全作業明細（作品名・話数・工程・完了ページ数・作業内容メモ）</li>
          <li>各作業明細に入力された<strong>作業データ保存先</strong>（本人が入力した場合、作業内容メモの下に「📁 保存先：〇〇」の形式で表示されます）</li>
          <li>引継ぎ・申し送りメモ（全文）</li>
          <li>添付画像（サムネイルをクリックして拡大確認）</li>
          <li>提出日時・更新日時</li>
        </ul>

        <div className="help-note">
          作業データの場所が分からない場合は、まずこの「保存先」欄を確認してください。パスが未入力、または分かりにくい場合は、添付画像に作業フォルダのスクリーンショットが含まれていないか確認し、それでも不明な場合は本人に直接確認してください。
        </div>

        <hr className="help-sep" />

        <h4>ダッシュボードでの日報確認</h4>
        <p>ダッシュボードには「<strong>最近の日報</strong>」パネルがあり、直近に提出された日報をすばやく確認できます。引継ぎメモ付きの日報は特に見落としのないよう確認してください。</p>

        <hr className="help-sep" />

        <h4>管理者が日報を代理入力する</h4>
        <p>「日報一覧」タブの右上にある「<strong>+ 日報入力</strong>」ボタンから、管理者が代理で日報を入力できます。アシスタントが記録を忘れた場合などに使います。アシスタントを選択した上で日報の内容を入力します。</p>

        <div className="help-note">
          代理入力した日報は通常の日報と同様に扱われます。アシスタント側の日報一覧にも表示されます。
        </div>

        <hr className="help-sep" />

        <h4>工程進捗への反映</h4>
        <p>日報一覧タブの上部には「<strong>未反映の工程進捗候補</strong>」パネルが表示されることがあります。日報に記録された完了ページ数が、作品の工程進捗にまだ反映されていない場合に表示されます。確認して適宜進捗に反映させてください。</p>

        <h4>日報を削除したときの工程進捗の扱い</h4>
        <p>日報を削除すると、その日報に紐づく工程進捗の反映履歴（反映ログ）も合わせて削除されます。</p>
        <ul>
          <li>削除する日報の内容が既に工程進捗（完了ページ数）に反映済みで、かつ<strong>反映後にその工程の進捗が他から更新されていなければ</strong>、反映前の状態まで進捗ページ数を安全に巻き戻します</li>
          <li>反映後に別の更新（他の日報の反映や手動修正など）が入っている場合は、どちらの変更を巻き戻すべきか判断できないため、安全のため進捗ページ数は<strong>巻き戻さず</strong>、反映ログの削除のみ行います</li>
        </ul>
        <div className="help-note">
          この挙動は、工程進捗パネルの「反映を取り消す」操作と同じ安全ロジックです。ヘルプスタッフの日報（ヘルプ勤務エントリ）を無効化・内容リセットした場合も同様に扱われます。
        </div>

        <hr className="help-sep" />

        <h4>日報が表示されない場合の確認ポイント</h4>
        <ul>
          <li>ページ上部の月選択が正しい月になっているか確認してください</li>
          <li>アシスタントが「日報を提出」せず「下書き保存」のままにしていると、管理者側には表示されません。アシスタントに確認してください</li>
          <li>退職済みアシスタントの日報も引き続き一覧に表示されます</li>
        </ul>
      </>
    ),
  },
  {
    id: 'payroll',
    title: '給与管理',
    content: (
      <>
        <p>「給与管理」ページでは、アシスタントさんへの給与明細を作成・確認・確定・支払済みにできます。「アシスタント」ページの支払準備とは別の管理です。給与明細という正式なフォーマットで記録したい場合に使います。</p>

        <h4>タブ一覧</h4>
        <table className="help-table">
          <thead><tr><th>タブ名</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td><strong>月次給与一覧</strong></td><td>指定月の給与明細一覧・新規作成・編集・プレビュー</td></tr>
            <tr><td><strong>税務設定</strong></td><td>アシスタントごとの税区分・扶養・年末調整の設定</td></tr>
            <tr><td><strong>源泉所得税集計</strong></td><td>年間の源泉所得税の月別納付状況・アシスタント別集計</td></tr>
          </tbody>
        </table>

        <hr className="help-sep" />

        <h4>勤務月と支払日の考え方</h4>
        <p>MangaOSの給与明細では「<strong>対象月</strong>（働いた月）」と「<strong>支払日</strong>（振り込む日）」を分けて管理しています。</p>
        <div className="help-note">
          例：2026年4月分の勤務 → 支払日は2026年5月10日。新規作成時に対象月を選ぶと、支払日が翌月10日に自動補完されます（手動変更も可能）。
        </div>

        <h4>給与明細のステータス</h4>
        <table className="help-table">
          <thead><tr><th>ステータス</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td><span className="badge badge-gray">下書き</span></td><td>作成中。まだ確定していない</td></tr>
            <tr><td><span className="badge badge-blue">確定</span></td><td>内容を確定した状態。アシスタントに明細を渡せる</td></tr>
            <tr><td><span className="badge badge-green">支払済</span></td><td>振込完了</td></tr>
            <tr><td><span className="badge badge-red">キャンセル</span></td><td>無効化済み。一覧には残るが集計対象外</td></tr>
          </tbody>
        </table>

        <hr className="help-sep" />

        <h4>給与明細を新規作成する</h4>
        <p>「月次給与一覧」タブ右上の「<strong>＋ 新規作成</strong>」ボタンから作成します。</p>
        <p>入力できる項目：</p>
        <ul>
          <li><strong>基本情報</strong>：アシスタント・対象月・支払日・発行者名</li>
          <li><strong>支給</strong>：基本給・時間外手当・役職手当・資格手当・住居手当・家族手当・通勤手当・立替交通費</li>
          <li><strong>控除</strong>：健康保険・厚生年金・雇用保険・所得税・住民税・財形貯蓄・生命保険料・積立金・組合費・その他控除</li>
          <li><strong>その他調整</strong>：年末調整還付・年末調整徴収・その他調整</li>
          <li><strong>勤怠情報</strong>：出勤日数・欠勤日数・有給日数・有給残日数・勤務時間・時間外労働時間</li>
          <li><strong>備考・ステータス</strong></li>
        </ul>
        <p>モーダル内に「差引支給額（自動計算）」がリアルタイムで表示されるので、入力内容を確認しながら進められます。</p>

        <h4>月次集計データからインポートする</h4>
        <p>「月次給与一覧」タブ内の「<strong>月次集計からインポート</strong>」ボタンを使うと、「アシスタント」ページの支払準備（月次支払集計）で作成したデータをもとに給与明細の下書きを一括生成できます。インポート後は各明細を開いて必要な項目（所得税など）を確認・修正してください。</p>

        <div className="help-note">
          インポート済みのデータがある月に再インポートするとスキップされます（重複作成されません）。
        </div>

        <h4>給与明細のプレビューと印刷</h4>
        <p>一覧の「<strong>プレビュー</strong>」ボタンをクリックすると、給与明細の印刷用レイアウトが表示されます。ブラウザの印刷機能（Ctrl+P / Cmd+P）でPDF出力や印刷ができます。</p>

        <h4>一括ステータス変更</h4>
        <p>タブ右上の「<strong>一括変更</strong>」機能を使うと、当月の全明細のステータスをまとめて変更できます。確定が終わった月の明細をまとめて「支払済」にする場合などに使います。</p>

        <div className="help-caution">
          一括変更は取り消せません。変更前に一覧の内容を確認してから実行してください。
        </div>

        <hr className="help-sep" />

        <h4>税務設定タブ</h4>
        <p>アシスタントごと・年度ごとの税務情報を管理します。給与明細の所得税欄に影響します。</p>
        <p>設定できる項目：</p>
        <table className="help-table">
          <thead><tr><th>項目</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td>税区分</td><td>甲欄（扶養控除等申告書あり）/ 乙欄（なし）/ 丙欄</td></tr>
            <tr><td>扶養控除等申告書</td><td>提出済み / 未提出</td></tr>
            <tr><td>扶養親族等の人数</td><td>扶養している家族の人数</td></tr>
            <tr><td>年末調整</td><td>対象 / 対象外</td></tr>
            <tr><td>源泉徴収票発行</td><td>対象 / 対象外</td></tr>
            <tr><td>申告書提出日</td><td>扶養控除等申告書を受け取った日</td></tr>
          </tbody>
        </table>

        <div className="help-caution">
          扶養控除等申告書が未提出（乙欄）の場合、税率が高くなります。アシスタントから申告書を受け取ったら忘れずに更新してください。また、前年度の設定のまま翌年に入った場合は警告が表示されます。毎年1月に各アシスタントの年度設定を確認してください。
        </div>

        <hr className="help-sep" />

        <h4>源泉所得税集計タブ</h4>
        <p>年間を通じた源泉所得税の集計が確認できます。年度を選んで表示します。</p>
        <ul>
          <li>月別の納付管理（確定済み給与明細の所得税合計）</li>
          <li>アシスタント別の年間集計（支払回数・課税支給額合計・源泉所得税合計など）</li>
        </ul>
        <p>「<strong>アシスタント別集計CSV</strong>」ボタンから年間集計のCSVをダウンロードできます。源泉徴収票の作成時や年末調整の確認に活用してください。</p>
      </>
    ),
  },
  {
    id: 'help-staff',
    title: 'ヘルプスタッフ管理',
    content: (
      <>
        <p>「<strong>ヘルプスタッフ</strong>」ページ（サイドバー「ヘルプスタッフ」）は、筒井さん・佐々木さん・高橋さんのように<strong>単発で勤務する人</strong>を管理するための機能です。通常アシスタントとは完全に別の管理の仕組みになっています。</p>

        <h4>ヘルプスタッフとは</h4>
        <ul>
          <li>単発勤務の人（ヘルプスタッフ）を、通常アシスタントとは<strong>別枠</strong>で管理する機能です</li>
          <li>通常アシスタントの一覧・シフト作成対象には混ざりません</li>
          <li>ヘルプスタッフ本人用の<strong>ログインアカウントは作りません</strong></li>
          <li>管理者が勤務を作成し、本人用の<strong>提出リンク</strong>を発行します</li>
          <li>本人はそのリンクから、自分の勤務・日報だけを提出できます（他人の日報や管理画面は見られません）</li>
        </ul>

        <h4>通常アシスタントとの違い</h4>
        <table className="help-table">
          <thead><tr><th>項目</th><th>通常アシスタント</th><th>ヘルプスタッフ</th></tr></thead>
          <tbody>
            <tr><td>管理場所</td><td>「アシスタント」ページ</td><td>「ヘルプスタッフ」ページ</td></tr>
            <tr><td>ログインアカウント</td><td>あり</td><td>なし（提出リンクのみ）</td></tr>
            <tr><td>シフト作成対象</td><td>対象になる</td><td>対象にならない</td></tr>
            <tr><td>日報の提出方法</td><td>ログインして日報ページから提出</td><td>管理者が発行した一時提出リンクから提出</td></tr>
            <tr><td>給与の扱い</td><td>給与管理・支払準備（payroll_records）</td><td>ヘルプスタッフ管理の月次集計（別テーブル）</td></tr>
          </tbody>
        </table>

        <hr className="help-sep" />

        <h4>ヘルプ勤務を作成する（管理者の手順）</h4>
        <ol>
          <li>サイドメニューから「<strong>ヘルプスタッフ</strong>」を開く</li>
          <li>「<strong>スタッフ管理</strong>」タブでヘルプスタッフを登録する（初回のみ。名前・デフォルト単価・デフォルト交通費・デフォルト残業時給を設定）</li>
          <li>「<strong>勤務管理</strong>」タブで対象月を選ぶ</li>
          <li>「<strong>+ ヘルプ勤務を作成</strong>」ボタンをクリックする</li>
          <li>スタッフ・勤務日・支払対象月・単価・交通費などを設定して保存する</li>
        </ol>

        <h4>本人提出リンクを発行する</h4>
        <ol>
          <li>勤務管理タブの一覧で対象の行の「<strong>リンク発行</strong>」ボタンをクリックする</li>
          <li>発行されたURLが画面に表示されるので、その場でコピーして本人へ送る</li>
        </ol>
        <div className="help-caution">
          発行されたURLは<strong>この画面を閉じると再表示できません</strong>。必ずその場でコピーしてください。また、リンクを知っている人は誰でも提出フォームにアクセスできるため、不要になったら一覧の「<strong>リンク無効化</strong>」ボタンで無効化してください。新しいリンクを再発行すると、古いリンクは自動的に無効になります。
        </div>

        <hr className="help-sep" />

        <h4>本人が提出する内容（提出フォーム）</h4>
        <p>本人が開く提出フォームのタイトルは「<strong>〇〇さんの勤務・日報提出フォーム</strong>」で、次の4セクションで構成されています。</p>

        <table className="help-table">
          <thead><tr><th>セクション</th><th>入力項目</th></tr></thead>
          <tbody>
            <tr><td><strong>勤務情報</strong></td><td>勤務日・開始時刻・終了時刻・休憩時間・自動計算された残業時間・勤務備考</td></tr>
            <tr><td><strong>作業日報</strong></td><td>作品名・話数・工程・完了ページ数・作業内容・作業データ保存先（複数作業を追加可）・引継ぎ／連絡事項</td></tr>
            <tr><td><strong>経費申告</strong></td><td>実費交通費・その他経費・経費メモ</td></tr>
            <tr><td><strong>作業画像（任意 / 複数可）</strong></td><td>作業内容がわかる画像を複数枚アップロード可能。アップロード後に個別削除も可能</td></tr>
          </tbody>
        </table>

        <div className="help-note">
          残業時間は開始時刻・終了時刻・休憩時間から<strong>自動計算</strong>されます。本人が残業時間を直接入力する項目はありません（詳しくは後述の「勤務時間・残業時間の扱い」を参照）。
        </div>

        <p>アップロードされた作業画像は、通常アシスタントの日報一覧の詳細表示でも確認できます（金額情報は表示されません）。</p>

        <hr className="help-sep" />

        <h4>一時保存と最終提出の違い</h4>
        <p>本人提出フォームには「<strong>一時保存する</strong>」と「<strong>勤務・日報を提出する</strong>」の2つのボタンがあります。</p>

        <table className="help-table">
          <thead><tr><th></th><th>一時保存</th><th>最終提出</th></tr></thead>
          <tbody>
            <tr><td>必須項目</td><td>すべて埋まっていなくても保存可</td><td>作業内容など必須項目が必要</td></tr>
            <tr><td>再編集</td><td>同じリンクから何度でも続きを入力可能</td><td>提出後の再編集は「再提出」として同じ最終提出ボタンから行う</td></tr>
            <tr><td>通常アシスタントの日報一覧</td><td>表示されない</td><td>表示される</td></tr>
            <tr><td>工程進捗の反映候補</td><td>出ない</td><td>出る（未確認〜確認済みの間）</td></tr>
            <tr><td>支払対象としての強調</td><td>強調されない</td><td>統合月次一覧などで通常表示される</td></tr>
          </tbody>
        </table>

        <div className="help-note">
          一時保存できるのは、勤務の状態が「<strong>未提出</strong>」または「<strong>一時保存</strong>」のときだけです。一度「勤務・日報を提出する」を押して最終提出した後は、一時保存ボタンは表示されなくなり、修正する場合は「<strong>勤務・日報を再提出する</strong>」ボタンから最終提出として更新します。
        </div>

        <p>管理者が確認済み・支払済みにした後は、本人はこのリンクから編集できなくなります。</p>

        <hr className="help-sep" />

        <h4>勤務時間・残業時間の扱い</h4>
        <p>残業時間は本人が手入力するのではなく、次の式で<strong>サーバー側が自動計算</strong>します。本人が入力した値をそのまま信用せず、保存のたびに開始時刻・終了時刻・休憩時間から必ず再計算します。</p>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--paper-2)',
          border: '1px solid var(--paper-3)', borderRadius: 8, padding: '10px 14px', margin: '10px 0',
        }}>
          自己申告残業時間 = 実働時間（終了時刻 − 開始時刻 − 休憩時間） − 標準勤務時間（510分 / 8.5時間）<br />
          ※ マイナスになる場合は0分
        </div>
        <p>この自己申告残業時間はあくまで本人申告の記録用の値です。実際の給与計算には、後述の「管理者確定残業時間」を使います。</p>

        <hr className="help-sep" />

        <h4>交通費・その他経費の扱い</h4>
        <p>交通費・その他経費・残業時間は、いずれも「<strong>本人申告値</strong>」と「<strong>管理者確定値</strong>」を分けて保存しています。</p>
        <ul>
          <li>本人申告値：本人が提出フォームで入力した値（そのままでは支払計算に使いません）</li>
          <li>管理者確定値：管理者がヘルプスタッフ管理画面の「勤務管理」から確認・入力する値</li>
        </ul>
        <div className="help-note">
          管理者確定欄の隣にある「<strong>コピー</strong>」ボタンを使うと、本人申告値をそのまま管理者確定欄にコピーできます。内容を確認して問題なければコピー、修正が必要であれば直接金額を書き換えてください。
        </div>

        <hr className="help-sep" />

        <h4>管理者確認・支払済み操作</h4>
        <p>ヘルプ勤務は「<strong>勤務管理</strong>」タブの一覧で状態を確認・操作します。</p>
        <table className="help-table">
          <thead><tr><th>ステータス</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td><span className="badge badge-gray">未提出</span></td><td>本人がまだ何も提出していない状態</td></tr>
            <tr><td><span className="badge badge-amber">一時保存</span></td><td>本人が入力途中で一時保存した状態（最終提出ではない）</td></tr>
            <tr><td><span className="badge badge-blue">提出済み</span></td><td>本人が最終提出した状態</td></tr>
            <tr><td><span className="badge badge-green">確認済み</span></td><td>管理者が内容を確認した状態</td></tr>
            <tr><td><span className="badge badge-red">無効化</span></td><td>管理者が無効化した状態（一覧・集計対象から除外）</td></tr>
          </tbody>
        </table>

        <ol>
          <li>本人申告交通費・本人申告その他経費・本人申告残業時間を確認する</li>
          <li>必要に応じて確定交通費・確定その他経費・確定残業時間／残業時給を入力（またはコピー）する</li>
          <li>内容に問題がなければ「<strong>確認済みにする</strong>」ボタンをクリックする</li>
          <li>実際に振込・支払いが完了したら「<strong>支払済みにする</strong>」ボタンをクリックする</li>
        </ol>

        <div className="help-caution">
          「確認済み」または「支払済み」にした後は、本人は提出リンクから内容を編集できなくなります。修正が必要な場合は、ヘルプスタッフ管理画面（管理者側）から直接編集してください。
        </div>

        <p>支払・人件費集計には、本人申告値ではなく<strong>管理者確定値</strong>のみが使われます。計算式は次のとおりです。</p>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--paper-2)',
          border: '1px solid var(--paper-3)', borderRadius: 8, padding: '10px 14px', margin: '10px 0',
        }}>
          ヘルプ給与額 = 単価 + 確定交通費 + 確定その他経費 + 残業代<br />
          残業代 = 確定残業時間 × 残業時給 ÷ 60
        </div>
        <div className="help-note">
          残業時給が0円の間は残業代が発生しません（記録用として残業時間だけ記録しておく運用も可能です）。
        </div>

        <hr className="help-sep" />

        <h4>無効化・復帰・内容リセット</h4>
        <p>ヘルプ勤務はデータを物理的に削除せず、状態を切り替えることで管理します。</p>

        <p><strong>無効化</strong></p>
        <ul>
          <li>誤って作成した勤務や、使わなくなった勤務を「<strong>無効化</strong>」できます（一覧の「✕」ボタン）</li>
          <li>無効化した勤務は、通常の一覧・支払集計・人件費集計の対象から除外されます</li>
          <li>無効化理由を任意で記録できます</li>
          <li>すでに支払済みのエントリを無効化する場合は、確認チェックが必要です</li>
        </ul>

        <p><strong>復帰</strong></p>
        <ul>
          <li>無効化した勤務は一覧の「<strong>復帰</strong>」ボタンから元に戻せます</li>
          <li>復帰時に「<strong>内容を保持して復帰</strong>」（無効化前の提出内容をそのまま残す）と「<strong>内容をリセットして復帰</strong>」（日報・勤怠・経費申告・画像を空に戻し未提出状態に戻す）のどちらかを選べます</li>
        </ul>

        <p><strong>内容リセット</strong></p>
        <ul>
          <li>一覧の「<strong>内容リセット</strong>」ボタンから、勤務エントリ自体は残したまま、本人提出内容（日報・勤怠申告・経費申告・作業画像）だけを空に戻せます</li>
          <li>単価・確定交通費・確定残業時給などの管理者側の設定項目はリセットされません</li>
          <li>その勤務の作業明細が工程進捗にすでに反映済みの場合、反映内容は安全に巻き戻されます</li>
          <li>すでに<strong>支払済み</strong>のエントリをリセットする場合は確認チェックが必要です。支払済みデータのリセットは特に慎重に行ってください</li>
        </ul>

        <hr className="help-sep" />

        <h4>日報一覧への表示</h4>
        <ul>
          <li>最終提出済み（提出済み・確認済み）のヘルプ日報は、通常アシスタントの「日報一覧」にも表示されます（引継ぎ内容を他のスタッフと共有するためです）</li>
          <li>一覧には「<strong>ヘルプ</strong>」バッジが付き、通常アシスタントの日報と区別できます</li>
          <li>単価・交通費・残業代などの<strong>金額情報は表示されません</strong></li>
          <li>通常アシスタント側の日報一覧からは編集・削除できません。修正が必要な場合はヘルプスタッフ管理画面から行ってください</li>
        </ul>

        <h4>工程進捗への反映</h4>
        <ul>
          <li>ヘルプ日報の作業明細（作品・話数・完了ページ数）も、通常日報と同じように工程進捗の反映候補に出ます</li>
          <li>対象は最終提出済み（提出済み・確認済み）のヘルプ日報のみです。<strong>一時保存中・無効化済みのものは反映候補に出ません</strong></li>
          <li>反映履歴には担当者名が「<strong>スタッフ名（ヘルプ）</strong>」の形式で表示されます</li>
          <li>内容リセット・無効化を行うと、その勤務にひもづく反映済みの進捗も合わせて巻き戻されます。反映後にリセット・無効化する場合は工程側の状況も確認してください</li>
        </ul>

        <hr className="help-sep" />

        <h4>ダッシュボード・給与管理・年次集計への反映</h4>

        <p><strong>ダッシュボード</strong></p>
        <ul>
          <li>ヘルプスタッフの費用（管理者確定値ベース）は、月次の人件費に反映されます</li>
          <li>事業用口座の未払い給与見込みには、「確認済み・かつ未払い」のヘルプ費用も反映されます</li>
          <li>通常アシスタント分とヘルプスタッフ分は内訳として別々に確認できます</li>
        </ul>

        <p><strong>給与管理</strong></p>
        <ul>
          <li>「給与管理」ページの「<strong>統合月次一覧</strong>」タブで、通常アシスタントとヘルプスタッフを同じ月次一覧で確認できます</li>
          <li>区分列に「<strong>通常アシスタント</strong>」「<strong>ヘルプスタッフ</strong>」がそれぞれ表示されます</li>
          <li>ヘルプスタッフ分は行の「<strong>ヘルプ給与明細</strong>」ボタンから内訳（単価・交通費・その他経費・残業代）を確認できます</li>
          <li>この一覧は読み取り専用です。編集はヘルプスタッフ管理画面（ヘルプスタッフ分）・給与管理／アシスタントページ（通常アシスタント分）からそれぞれ行います</li>
        </ul>

        <p><strong>年間支払集計</strong></p>
        <ul>
          <li>「ヘルプスタッフ」ページの「<strong>年間支払集計</strong>」タブで、年を指定してスタッフ別・月別の支払状況を確認できます</li>
          <li>「支払済みのみ」「確認済みを含む」で表示範囲を切り替えられます</li>
        </ul>
        <div className="help-note">
          年間支払集計は社内管理用の集計です。源泉徴収票・支払調書のような正式な税務帳票ではありません。
        </div>

        <hr className="help-sep" />

        <h4>注意点</h4>
        <ul>
          <li>ヘルプスタッフは通常アシスタントのシフト作成対象にはなりません</li>
          <li>ヘルプスタッフ本人用の通常ログインアカウントはありません（提出リンクのみでアクセスします）</li>
          <li>提出リンクは本人専用です。不要になったら「リンク無効化」してください</li>
          <li>一時保存中の内容は、日報一覧への共有・工程進捗反映・支払対象としての強調のいずれの対象にもなりません</li>
          <li>管理者が「確認済み」「支払済み」にした後は、本人はリンクから編集できません</li>
          <li>支払・人件費の金額集計には、本人申告値ではなく<strong>管理者確定値</strong>を使います</li>
          <li>ヘルプ日報は通常アシスタントの日報一覧にも共有されますが、金額情報は出ません</li>
          <li>テストでヘルプ勤務データを作成・削除する場合は、発行済みの提出リンク（トークン）・アップロード済み画像・工程進捗への反映履歴が残っていないか確認してください</li>
        </ul>
      </>
    ),
  },
  {
    id: 'finance',
    title: '収入・支払・月次レポート',
    content: (
      <>
        <p>MangaOSでは、収入・支払・月次レポートがそれぞれ独立したページで管理されています。月末の締め作業に向けて、以下の順で確認するとスムーズです。</p>
        <ol>
          <li>収入ページで入金を確認・記録する</li>
          <li>支払管理ページで未払い支出を処理する</li>
          <li>月次レポートで全体を確認し、締め処理を実行する</li>
        </ol>

        <hr className="help-sep" />

        <h4>収入管理（収入・税積立ページ）</h4>
        <p>「収入・税積立」ページでは、売上・入金の記録と税積立の管理を行います。</p>

        <p>ページ上部のサマリーで確認できる指標：</p>
        <table className="help-table">
          <thead><tr><th>指標</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>当月入金済</td><td>その月に「入金済み」として登録された収入の合計</td></tr>
            <tr><td>税積立額</td><td>収入に対して先取りで積み立てる税金の見込み額</td></tr>
            <tr><td>税引後手取り</td><td>入金済み − 税積立額</td></tr>
            <tr><td>内訳</td><td>原稿料・印税・カイカイキキなどの種別ごとの合計</td></tr>
          </tbody>
        </table>

        <h4>収入を追加する</h4>
        <p>右上の「<strong>+ 収入追加</strong>」ボタンから登録します。入力項目：</p>
        <ul>
          <li><strong>収入種別</strong>：原稿料 / 印税 / カイカイキキ / 先払い / その他</li>
          <li><strong>作品</strong>（任意）：どの作品の収入か</li>
          <li><strong>金額</strong>（必須）</li>
          <li><strong>税積立率（%）</strong>：この収入に対して税金として先取りする割合。入力すると「税積立額」と「税引後額」がプレビュー表示されます</li>
          <li><strong>入金予定日</strong>：実際の振込が予定されている日</li>
          <li><strong>実際の入金日</strong>：入金済みの場合は記入</li>
          <li><strong>備考</strong>：例「週刊A 101話 原稿料」</li>
        </ul>

        <div className="help-note">
          「入金予定日」のみ入力した状態は「入金待ち」です。実際に振込を確認したら「<strong>入金済にする</strong>」ボタンをクリックするか、「実際の入金日」を入力してください。入金済みになって初めて月次レポートの「入金済み」に計上されます。
        </div>

        <div className="help-note">
          収入追加モーダルは枠外クリックでは閉じません。閉じる場合は「✕」または「キャンセル」を使ってください（保存成功時は自動で閉じます）。
        </div>

        <div className="help-caution">
          入金予定日と実際の入金日は別物です。予定日だけ入力した状態では「未入金」扱いのままです。混同しないよう注意してください。
        </div>

        <h4>収入の編集・削除</h4>
        <p>各行の「<strong>編集</strong>」ボタンから金額・税積立率・日付・備考を修正できます。削除は「<strong>削除</strong>」ボタンから行えます（確認ダイアログが表示されます）。入金済みの収入を削除する場合は特に注意してください。</p>

        <hr className="help-sep" />

        <h4>支払管理（支払管理ページ）</h4>
        <p>「支払管理」ページでは、スタジオの支出（経費・固定費など）を記録・管理します。</p>

        <h4>支出を追加する</h4>
        <p>右上の「<strong>+ 支出を追加</strong>」ボタンから登録します。入力項目：</p>
        <ul>
          <li><strong>カテゴリ</strong>：国民年金 / 国民健康保険 / 家賃 / クレジットカード / 確定拠出年金 / アシスタント給与 / 交通費 / 食費 / 修復学校 / その他固定費 / その他変動費</li>
          <li><strong>支払方法</strong>：口座引落 / クレジットカード / 振込 / 現金</li>
          <li><strong>支払元口座</strong>：事業用口座 / 漫画家用個人口座</li>
          <li><strong>支払区分</strong>：事業費 / 税金 / 事業費にできない支払い</li>
          <li><strong>内容</strong>（必須）：何の支払いか</li>
          <li><strong>金額</strong>（必須）</li>
          <li><strong>支払期日</strong>：支払う予定の日付</li>
          <li><strong>対象月</strong>：費用が発生した月（会計上の帰属月）</li>
        </ul>

        <div className="help-note">
          支出追加モーダルは枠外クリックでは閉じません。閉じる場合は「✕」または「キャンセル」を使ってください（保存成功時は自動で閉じます）。
        </div>

        <h4>支払済みにする</h4>
        <p>各行の「<strong>支払済</strong>」ボタンをクリックすると支払い完了として記録されます。支払済みにすると月次締め後に変更できなくなります。</p>

        <div className="help-caution">
          月次締め後は支出の支払状態を変更できません。締め前に未払い支出の処理を完了させてください。
        </div>

        <hr className="help-sep" />

        <h4>固定費テンプレート（固定費設定ページ）</h4>
        <p>固定費テンプレートは「繰り返し発生する支出」の元データです。テンプレートを登録しておくと、月次の支払予定を一括生成できます。</p>

        <h4>テンプレートを追加・編集する</h4>
        <p>一覧右上の「<strong>+ 新規作成</strong>」ボタンから登録します。既存テンプレートの修正は各行の「<strong>編集</strong>」ボタンから行います。入力項目：</p>
        <ul>
          <li><strong>カテゴリ</strong>・<strong>説明</strong>・<strong>支払方法</strong>・<strong>支払元口座</strong>（事業用口座 / 漫画家用個人口座）</li>
          <li><strong>繰り返し方法</strong>：「<strong>毎年繰り返す</strong>」または「<strong>指定年月だけ発生する</strong>」から選ぶ</li>
        </ul>

        <p><strong>「毎年繰り返す」の場合</strong></p>
        <ul>
          <li>金額・支払日は共通の1つの値を設定します</li>
          <li>生成対象月をチェックボックスで選びます。「<strong>すべて選択</strong>」「<strong>すべて解除</strong>」のほか、「<strong>7月〜2月（国保税）</strong>」のような季節性の固定費向けプリセットボタンがあります</li>
        </ul>

        <p><strong>「指定年月だけ発生する」の場合</strong></p>
        <ul>
          <li>「<strong>＋ 行を追加</strong>」ボタンで、対象年月・金額・支払日をそれぞれ個別に持つ行を必要な数だけ追加できます</li>
          <li>1回だけ発生する支出は行を1つだけ追加します</li>
          <li>年をまたぐ場合や、月によって金額・支払日が異なる場合も、行ごとに別の値を設定すれば対応できます（例：2027年1月と2月は2日引き落としで3,000円、2026年7月は31日引き落としで3,400円、など）</li>
        </ul>

        <div className="help-caution">
          テンプレート作成・編集モーダルは、誤って入力内容を失わないよう<strong>枠外クリックでは閉じません</strong>。閉じる場合は右上の「✕」または「キャンセル」を使ってください。保存に成功すると自動で閉じます。
        </div>

        <h4>テンプレート一覧の並び替え・削除・無効化</h4>
        <ul>
          <li>一覧の各列見出しをクリックすると、その列で昇順・降順に並び替えられます</li>
          <li>「<strong>削除</strong>」：テンプレート本体を完全に削除します。過去に生成済みの支払予定（支払済みを含む）は expenses 側の独立したデータなので、テンプレートを削除しても一切変更・削除されません</li>
          <li>「<strong>無効化</strong>」／「<strong>再有効化</strong>」：テンプレートのデータ自体は残したまま、以後の月次生成の対象からだけ外したい場合はこちらを使います</li>
        </ul>

        <h4>月次支払予定を生成する</h4>
        <p>固定費設定ページの「<strong>月次支払予定の生成</strong>」パネルで、対象月を選んで「<strong>〇〇 の支払予定を生成</strong>」ボタンをクリックすると、有効なテンプレートをもとにその月の支払予定（支出）が一括作成されます。</p>
        <p>生成結果には「<strong>作成〇件</strong>」「<strong>スキップ〇件</strong>」のバッジと、テンプレートごとの結果一覧（✓ 作成 / 対象外月 / スキップ（生成済み））が表示されます。既に生成済みのものは重複作成されません。</p>

        <div className="help-caution">
          同じ月に何度「生成」を押しても重複作成されない仕組みですが、念のため生成後に支払管理ページで内容を確認してください。テンプレートを変更した場合、既に生成済みの支払予定は自動更新されないため古い内容のまま残ることがあります。
        </div>

        <hr className="help-sep" />

        <h4>月次レポート</h4>
        <p>「月次レポート」ページでは、指定月の収支・人件費・シフト・制作状況をまとめて確認できます。月末の確認作業と月次締め処理もここから行います。</p>

        <h4>確認できる項目</h4>
        <table className="help-table">
          <thead><tr><th>セクション</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td>収支サマリー</td><td>入金済み・税積立・支出合計・税引前利益・今使えるお金・未入金・未払い支出・人件費比率</td></tr>
            <tr><td>人件費</td><td>人件費合計（食費込）・人足数・出勤日数・稼働人数・目標上限との比較バー</td></tr>
            <tr><td>月次支払</td><td>アシスタントへの支払合計・支払済件数・未確認件数</td></tr>
            <tr><td>制作・締切</td><td>進行中の話数と締切までの残日数</td></tr>
            <tr><td>シフト状況</td><td>シフト希望提出率・確定済み人数</td></tr>
            <tr><td>作品別収益</td><td>各作品の入金済み額と未入金額</td></tr>
          </tbody>
        </table>

        <h4>月次締めの手順</h4>
        <p>締め処理は右上のボタンから2段階で実行します。</p>
        <ol>
          <li>「<strong>レビュー開始</strong>」ボタンをクリックする（ステータスが「📋 レビュー中」になる）</li>
          <li>内容を確認したら「<strong>✓ 承認・締め処理</strong>」ボタンをクリックする</li>
          <li>確認ダイアログが表示されるので「<strong>承認・締める</strong>」をクリックして確定する</li>
        </ol>

        <p>締め後にできること・できないこと：</p>
        <table className="help-table">
          <thead><tr><th>操作</th><th>締め後</th></tr></thead>
          <tbody>
            <tr><td>アシスタントへの支払完了記録</td><td>✓ できる</td></tr>
            <tr><td>CSV出力</td><td>✓ できる</td></tr>
            <tr><td>固定費・支出の支払状態変更</td><td>✕ できない</td></tr>
            <tr><td>勤怠の追加・変更</td><td>✕ できない</td></tr>
          </tbody>
        </table>

        <div className="help-caution">
          締め処理は「その月の確認作業が完了した」という最終確定に近い操作です。締め後に誤りに気づいた場合は「<strong>締めを解除</strong>」ボタンで再オープンできますが、解除理由の入力が必要です。解除後に修正して再度締め処理を実行してください。
        </div>

        <h4>締め前に確認すべきポイント</h4>
        <ul>
          <li>「アシスタント」→「支払準備」タブで未払いの人がいないか</li>
          <li>収入ページで入金済みにし忘れている収入がないか</li>
          <li>支払管理ページで未払い支出が残っていないか</li>
          <li>月次レポートの「未払い支出」や「未確認」件数がゼロになっているか</li>
        </ul>

        <h4>レポートCSVのダウンロード</h4>
        <p>右上の「<strong>レポートCSV</strong>」ボタンから月次の集計データをCSV形式でダウンロードできます。</p>

        <hr className="help-sep" />

        <h4>口座残高の調整</h4>
        <p>口座残高の管理は「<strong>ダッシュボード</strong>」ページで行います。「事業用口座」と「漫画家用個人口座」それぞれのカードに「<strong>残高調整</strong>」ボタンがあります。</p>
        <p>実際の銀行口座残高とシステム上の残高がずれた場合に使います。</p>
        <ul>
          <li>増加させる場合：プラスの金額を入力（例：+100000）</li>
          <li>減少させる場合：マイナスの金額を入力（例：-50000）</li>
          <li>理由を入力しておくと、後で確認しやすくなります（任意）</li>
        </ul>
        <div className="help-caution">
          残高調整は実際の支出・収入の記録ではなく、残高の「差額補正」です。毎月定常的に発生する収支は必ず収入・支払管理ページで正しく記録してください。残高調整は実口座との誤差修正にのみ使ってください。
        </div>
      </>
    ),
  },
  {
    id: 'works',
    title: '作品・話数・制作進捗',
    content: (
      <>
        <p>「作品・話数」ページは、連載作品の各話数ごとに制作工程の進捗・締切・ステータスを管理する画面です。日報が「今日の実績」を記録する場所であるのに対し、こちらは「現在どの話がどこまで進んでいるか」を把握するための場所です。</p>

        <h4>ページの構成</h4>
        <p>ページ上部に「今日の制作指示」パネルと「保存済み制作予定」パネルが表示されます。その下に作品タブと話数一覧があります。</p>

        <h4>作品タブ</h4>
        <p>登録されている作品ごとにタブが表示されます。「すべて」タブで全作品の話数を一覧表示できます。作品タブをクリックすると、その作品のページ単価・1話ページ数・1話原稿料・原作の有無が表示されます。</p>

        <h4>話数の種別</h4>
        <table className="help-table">
          <thead><tr><th>種別</th><th>ラベル例</th><th>主な用途</th></tr></thead>
          <tbody>
            <tr><td>ストーリー</td><td>#101</td><td>通常連載の各話</td></tr>
            <tr><td>単行本</td><td>単行本1巻 カバー修正</td><td>単行本作業・修正</td></tr>
            <tr><td>カラー</td><td>扉絵カラー</td><td>カラーイラスト作業</td></tr>
          </tbody>
        </table>

        <h4>話数のステータス</h4>
        <table className="help-table">
          <thead><tr><th>ステータス</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>予定</td><td>まだ作業が始まっていない</td></tr>
            <tr><td>進行中</td><td>いずれかの工程が作業中</td></tr>
            <tr><td>提出済</td><td>編集部へ提出完了</td></tr>
            <tr><td>完了</td><td>すべての工程が完了</td></tr>
            <tr><td>休載</td><td>休載となった話数</td></tr>
          </tbody>
        </table>

        <h4>制作工程（工程バー）</h4>
        <p>話数一覧の各行に、工程の進捗を示す小さなドット（工程バー）が表示されます。左から順に以下の工程に対応しています。</p>
        <table className="help-table">
          <thead><tr><th>工程</th><th>状態の色</th></tr></thead>
          <tbody>
            <tr><td>ネーム → ネーム修正 → 下書き → ペン入れ → 背景 → 仕上げ → 納品</td><td>未着手（灰）/ 作業中（青）/ 完了（緑）</td></tr>
          </tbody>
        </table>
        <p>ドットにカーソルを合わせると、その工程の名前と状態が表示されます。</p>

        <h4>締切チップの見方</h4>
        <table className="help-table">
          <thead><tr><th>表示</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>残N日（緑）</td><td>7日以上の余裕がある</td></tr>
            <tr><td>残N日（黄）</td><td>締切まで4〜7日</td></tr>
            <tr><td>残N日（赤）</td><td>締切まで3日以内</td></tr>
            <tr><td>今日（赤）</td><td>当日が締切</td></tr>
            <tr><td>N日超過（赤）</td><td>締切を過ぎている</td></tr>
          </tbody>
        </table>
        <p>「ネーム確認締切」「提出締切」「入稿日」のいずれかが設定されていて、かつ対応する工程が完了していない場合に表示されます。工程が完了すると自動的に非表示になります。</p>

        <h4>話数を追加する</h4>
        <ol>
          <li>右上の「<strong>+ 話数追加</strong>」ボタンをクリック</li>
          <li>作品・作業種別（ストーリー / 単行本 / カラー）を選択</li>
          <li>ストーリーの場合は「話数番号」、単行本・カラーの場合は「ラベル」を入力</li>
          <li>ページ数・各締切日を必要に応じて入力</li>
          <li>「追加」ボタンをクリック</li>
        </ol>

        <hr className="help-sep" />

        <h4>作品を追加・編集・アーカイブする（作品を管理）</h4>
        <p>作品自体の追加・編集・アーカイブ・復元・削除は、日常的に使う「＋ 話数追加」ボタンとは別に、右上の「<strong>作品を管理</strong>」ボタンから行います（誤って話数追加ボタンと押し間違えないよう、あえて別の場所に分けています）。</p>
        <p>「作品を管理」モーダル内の操作：</p>
        <table className="help-table">
          <thead><tr><th>ボタン</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td><strong>＋ 作品追加</strong></td><td>新しい作品を登録する（ページ単価・1話ページ数などを設定）</td></tr>
            <tr><td><strong>編集</strong></td><td>作品情報を修正する</td></tr>
            <tr><td><strong>アーカイブ</strong></td><td>連載終了・休止などで今後使わなくなった作品を一覧から外す</td></tr>
            <tr><td><strong>復元</strong></td><td>アーカイブ済みの作品を通常表示に戻す</td></tr>
            <tr><td><strong>削除</strong></td><td>作品を完全に削除する（確認ダイアログあり）</td></tr>
          </tbody>
        </table>

        <div className="help-note">
          作品をアーカイブすると、新規話数追加時の作品選択候補からは外れますが、その作品に紐づく過去の日報・給与・レポート上の作品名は引き続き正しく表示されます。アーカイブ済み作品は「作品を管理」モーダルで表示・復元できます。
        </div>

        <div className="help-caution">
          話数や過去データ（収益・打ち合わせ記録・制作計画など）が1件でも紐づいている作品は、削除できません（「この作品には話数または過去データが紐づいているため削除できません。代わりにアーカイブしてください。」と表示されます）。削除できるのは、誤って作成した中身が空の作品だけです。使わなくなった作品は基本的に「アーカイブ」を使ってください。
        </div>

        <h4>工程進捗を更新する</h4>
        <ol>
          <li>話数一覧の対象行をクリック（または「工程」列のドットをクリック）</li>
          <li>工程モーダルが開く</li>
          <li>各工程のボタン（「未着」「作業中」「完了」）をクリックしてステータスを切り替える</li>
          <li>下書き・ペン入れ・背景・仕上げは進捗ページ数・目標ページ数・メモを入力して「進捗保存」できる</li>
        </ol>
        <div className="help-note">
          すべての工程を「完了」にすると、話数全体のステータスが自動的に更新されます。
        </div>

        <h4>アーカイブ（完了済みの非表示）</h4>
        <p>「完了」または「提出済」の状態で、かつ提出締切から6か月以上経過した話数は自動的にアーカイブ扱いになり、通常表示から非表示になります。</p>
        <p>「<strong>完了済みN件を表示</strong>」ボタンをクリックすると、アーカイブ済みの話数も一覧に表示できます。</p>

        <h4>今日の制作指示パネル</h4>
        <p>ページ上部の「今日の制作指示」パネルには、締切・進捗に基づいて自動計算された「今日取り組むべき工程」が表示されます。</p>
        <table className="help-table">
          <thead><tr><th>リスクレベル</th><th>表示色</th><th>意味</th></tr></thead>
          <tbody>
            <tr><td>破綻見込み</td><td>赤</td><td>現在のペースでは間に合わない</td></tr>
            <tr><td>危険</td><td>赤</td><td>かなりタイトなスケジュール</td></tr>
            <tr><td>注意</td><td>黄</td><td>ペースアップが望ましい</td></tr>
            <tr><td>安全</td><td>緑</td><td>余裕がある</td></tr>
          </tbody>
        </table>
        <p>各項目には「余裕N日 / 締切日付」が表示されます。締切未設定の話数は表示されません。</p>

        <hr className="help-sep" />

        <h4>制作計画ページ（/works/production-plan）</h4>
        <p>「制作計画」ページでは、日付ごとの作業予定とキャパシティを管理できます。今日の制作指示パネル右上の「<strong>制作計画</strong>」ボタンから開けます。</p>
        <ul>
          <li>日付ごとに担当者と作業内容の予定が表示されます</li>
          <li>キャパシティバーで担当者の作業負荷（使用量 / キャパ）が確認できます</li>
          <li>負荷が100%を超えると黄〜赤で強調表示されます</li>
          <li>アシスタントは平日のみの稼働として計算されます（設定による）</li>
        </ul>
        <div className="help-note">
          制作計画は「これから行う予定を見る場所」です。日報が実績を記録する場所、シフトが人の稼働予定を管理する場所であるのに対して、制作計画は「いつ何の工程を進めるか」を把握するための場所です。
        </div>

        <h4>制作進行設定ページ（/works/progress-settings）</h4>
        <p>「制作進行設定」ページでは、制作計画の自動計算に使う2種類の設定を管理します。今日の制作指示パネル右上の「<strong>制作進行設定</strong>」ボタンから開けます。</p>

        <p><strong>工程別キャパ設定</strong></p>
        <p>各工程の1日あたりの作業量（キャパシティ）を工程と担当種別ごとに設定します。</p>
        <table className="help-table">
          <thead><tr><th>項目</th><th>説明</th></tr></thead>
          <tbody>
            <tr><td>通常キャパ</td><td>通常時の1日あたり作業量（P/日 または 日数）</td></tr>
            <tr><td>緊急キャパ</td><td>緊急時に使う上限値</td></tr>
            <tr><td>土日作業可</td><td>チェックすると土日も稼働日として計算</td></tr>
            <tr><td>並行可</td><td>他の工程と同時進行できるかどうか</td></tr>
            <tr><td>有効</td><td>チェックを外すと計算から除外</td></tr>
          </tbody>
        </table>

        <p><strong>単体作業ルール</strong></p>
        <p>単行本・カラーなど、ページ数ではなく日数単位で管理する作業のルールを設定します。</p>
        <table className="help-table">
          <thead><tr><th>項目</th><th>説明</th></tr></thead>
          <tbody>
            <tr><td>標準日数</td><td>通常かかる日数の目安</td></tr>
            <tr><td>ページノルマ有</td><td>ページ数入力が必要かどうか</td></tr>
          </tbody>
        </table>
        <p>変更後は「<strong>一括保存</strong>」ボタンをクリックしてください。</p>
        <div className="help-caution">
          制作進行設定を変更すると、今日の制作指示パネルや制作計画の計算結果が変わります。運用中に大きく変更する場合は、事前に制作計画への影響を確認してください。
        </div>
      </>
    ),
  },
  {
    id: 'settings',
    title: '設定・アカウント',
    content: (
      <>
        <p>アカウント設定とアシスタントアカウントの管理方法を説明します。</p>

        <h4>管理者アカウント設定（自分のアカウント）</h4>
        <p>右上のメールアドレスをクリックすると「アカウント設定」ページへ移動できます。</p>

        <p><strong>メールアドレスの変更</strong></p>
        <ol>
          <li>「メールアドレス変更」欄に新しいメールアドレスを入力</li>
          <li>「メールアドレスを変更」ボタンをクリック</li>
          <li>変更完了後、ページが自動的に再読み込みされます</li>
        </ol>

        <p><strong>パスワードの変更</strong></p>
        <ol>
          <li>「パスワード変更」欄に現在のパスワード・新しいパスワード（8文字以上）・確認用パスワードを入力</li>
          <li>「パスワードを変更」ボタンをクリック</li>
        </ol>
        <div className="help-caution">
          パスワードは8文字以上必要です。変更後は必ず新しいパスワードを安全な場所に保管してください。
        </div>

        <div className="help-note">
          初回ログイン時に「パスワードを変更してください」という画面が表示された場合は、管理者から受け取った初期パスワードを「現在のパスワード」に入力してください。変更後はホーム画面へ自動的に移動します。
        </div>

        <hr className="help-sep" />

        <h4>アシスタントアカウントの新規追加</h4>
        <p>新しいアシスタントを登録するには、アシスタントページの右上「<strong>人員管理 ▾</strong>」ボタンから「<strong>＋ 新しいアシスタントを追加</strong>」をクリックします。6ステップのウィザードが開きます。</p>

        <table className="help-table">
          <thead><tr><th>ステップ</th><th>内容</th></tr></thead>
          <tbody>
            <tr><td>Step 1</td><td>基本情報（氏名・メールアドレス・電話番号・LINE など）</td></tr>
            <tr><td>Step 2</td><td>アカウント設定（ログイン有効化・初期パスワードの自動生成）</td></tr>
            <tr><td>Step 3</td><td>勤務・給与設定（役割・日給・研修期間・交通費上限など）</td></tr>
            <tr><td>Step 4</td><td>日報・シフト提出の有効化設定</td></tr>
            <tr><td>Step 5</td><td>初回案内文の確認とコピー（LINEやメールで送る用）</td></tr>
            <tr><td>Step 6</td><td>内容確認・登録実行</td></tr>
          </tbody>
        </table>

        <div className="help-note">
          Step 5 で生成される「初回案内文」には、ログインURL・メールアドレス・初期パスワードが含まれます。コピーしてアシスタントさんにLINEやメールで送ってください。
        </div>

        <h4>仮メールアドレス（@mangaos.test）について</h4>
        <p>メールアドレスをまだ持っていないアシスタントさんや、後で本人に変更してもらう場合に備えて、<code>名前@mangaos.test</code> のような仮のメールアドレスを使って登録できます。</p>
        <ul>
          <li><code>@mangaos.test</code> のアドレスには実際にメールは届きません</li>
          <li>確定シフトの通知メールなども送信できません（ウィザードで「未登録」扱いになります）</li>
          <li>アシスタントさんがログイン後にアカウント設定から本メールに変更できます</li>
          <li>メール未設定のアシスタントがいる場合、シフト確定メール送信時に「メール未登録: N名」と表示されます</li>
        </ul>
        <div className="help-caution">
          仮メールのままだと、シフト確定の通知が届きません。アシスタントさんに本メールへの変更を促してください。
        </div>

        <h4>アシスタントアカウントの編集・管理</h4>
        <p>既存のアシスタント情報を変更するには、アシスタントページの「<strong>人員管理モードを開始</strong>」から各行の「<strong>編集</strong>」ボタンをクリックします。</p>
        <p>人員管理モードでは以下の操作が可能です：</p>
        <ul>
          <li>基本情報・給与設定の編集</li>
          <li>退職予定・退職済みへのステータス変更（「退職処理」ボタン）</li>
          <li>退職済みアシスタントの再有効化（「再有効化」ボタン）</li>
        </ul>

        <h4>退職済み・仮登録アシスタントの表示</h4>
        <p>通常表示では在籍中のアシスタントのみ表示されます。「<strong>退職予定・退職済みを表示</strong>」「<strong>退職者一覧</strong>」のメニュー項目から過去のアシスタントも確認できます。</p>
        <p>登録はしたがまだ設定が完了していないアシスタントには「<strong>仮登録</strong>」バッジが表示されます。</p>

        <h4>管理者とアシスタントで見える画面の違い</h4>
        <table className="help-table">
          <thead><tr><th>機能</th><th>管理者</th><th>アシスタント</th></tr></thead>
          <tbody>
            <tr><td>ダッシュボード</td><td>全員の状況・口座残高・KPI</td><td>自分の日報・シフト確認</td></tr>
            <tr><td>アシスタントページ</td><td>全タブ・全機能</td><td>閲覧不可</td></tr>
            <tr><td>給与管理・収入</td><td>全機能</td><td>閲覧不可</td></tr>
            <tr><td>作品・話数</td><td>追加・編集・削除・工程更新</td><td>閲覧のみ</td></tr>
            <tr><td>日報</td><td>全員分の閲覧・編集</td><td>自分の分のみ</td></tr>
            <tr><td>シフト</td><td>全員分の確認・確定操作</td><td>自分のシフト希望のみ</td></tr>
            <tr><td>管理者ヘルプ</td><td>表示される</td><td>表示されない</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'trouble',
    title: '困ったとき',
    content: (
      <>
        <h4>データが保存されない・エラーが出る</h4>
        <ul>
          <li>必須項目（<span style={{ color: 'var(--red)' }}>*</span>）がすべて入力されているか確認してください</li>
          <li>ページを再読み込み（F5）してから再操作してみてください</li>
          <li>「締め済み」の月に書き込もうとしていないか確認してください</li>
        </ul>

        <h4>月次締め後に修正が必要になった</h4>
        <ol>
          <li>月次レポートページで対象月を開く</li>
          <li>「<strong>締めを解除</strong>」ボタンをクリックする</li>
          <li>解除理由を入力して確定する</li>
          <li>修正作業を行う</li>
          <li>再度「<strong>承認・締め処理</strong>」を実行する</li>
        </ol>
        <div className="help-note">
          月次締め後でも「アシスタントへの支払完了記録」と「CSV出力」は引き続き行えます。締め後に変更できないのは「固定費・支出の支払状態変更」と「勤怠の追加・変更」です。
        </div>

        <h4>固定費を重複して生成してしまった</h4>
        <p>固定費テンプレートから月次生成を実行したとき、同じ月に同じテンプレートがすでに存在する場合は自動的にスキップされます（重複生成されません）。ただし誤って登録してしまった場合は、支払管理ページから対象の固定費を手動で削除してください。</p>
        <div className="help-caution">
          すでに「支払済」にした固定費を削除すると支出実績が変わります。月次レポートが締め済みの場合は解除してから対応してください。
        </div>

        <h4>収入や支出を間違えて登録した</h4>
        <ul>
          <li>収入管理・支払管理の各ページで対象行の「<strong>編集</strong>」から修正できます</li>
          <li>「入金済み」「支払済み」にしたあとでも編集可能ですが、月次が締め済みの場合は先に「締めを解除」してから修正してください</li>
          <li>削除する場合は行の「削除」ボタンをクリックしてください</li>
        </ul>

        <h4>日報が一覧に表示されない</h4>
        <ul>
          <li>アシスタントページの「日報一覧」タブで対象月を確認してください</li>
          <li>月選択が正しい月になっているか確認してください</li>
          <li>アシスタントさんがまだ日報を提出していない可能性があります。「給与サマリー」タブで勤怠記録の有無を確認してください</li>
        </ul>

        <h4>シフト希望が見つからない・提出されていない</h4>
        <ul>
          <li>アシスタントページの「シフト希望」タブで未提出者を確認できます</li>
          <li>ページ上部にある「MissingBanner」（未提出催促バナー）が表示されている場合は、そこから催促メールを送れます</li>
          <li>シフト希望の締切日を過ぎていると、アシスタント側で編集できなくなります。必要に応じて締切日を変更してください</li>
        </ul>

        <h4>確定シフトのメールを送れない</h4>
        <ul>
          <li>メールアドレスが <code>@mangaos.test</code> の仮メールになっているアシスタントには送信できません</li>
          <li>メール未登録のアシスタントがいる場合は「送信結果」に「未登録N名」と表示されます</li>
          <li>仮メールのアシスタントへはLINEなどの別手段で確定シフトを伝えてください</li>
        </ul>

        <h4>給与申告が未提出のまま締めたい</h4>
        <ul>
          <li>「給与申告確認」タブで未提出者を確認できます</li>
          <li>提出がない場合でも月次締めは実行可能です（未提出者の給与は確定されない状態で締まります）</li>
          <li>締め前に該当アシスタントさんへ直接確認することをお勧めします</li>
        </ul>

        <h4>勤怠や給与を修正したい</h4>
        <ul>
          <li>アシスタントページ「給与サマリー」タブの各勤怠行で「<strong>編集</strong>」または「<strong>削除</strong>」が可能です</li>
          <li>月次が締め済みの場合は先に「締めを解除」してから修正してください</li>
        </ul>

        <h4>作品・話数の進捗が実態と合わない</h4>
        <ul>
          <li>話数をクリックして工程モーダルを開き、各工程のステータスを正しい状態に更新してください</li>
          <li>進捗ページ数を入力することで、より正確な完了見込みが計算されます</li>
          <li>締切日が古いままになっている場合は、話数の編集から更新してください</li>
        </ul>

        <h4>アシスタントがログインできないと連絡が来た</h4>
        <ul>
          <li>アシスタントページ（人員管理モード）でそのアシスタントのメールアドレスを確認してください</li>
          <li>仮メール（<code>@mangaos.test</code>）のままになっていないか確認してください</li>
          <li>退職処理が誤って実行されていないか確認してください</li>
          <li>パスワードを忘れた場合は、アシスタントさん自身のアカウント設定からパスワードを変更してもらってください（現在のパスワードが必要です）</li>
        </ul>

        <h4>スマートフォンで見づらい</h4>
        <ul>
          <li>MangaOS はスマートフォンにも対応しています</li>
          <li>ナビゲーションは左上の「☰」（ハンバーガーメニュー）から開けます</li>
          <li>テーブルは横スクロールで見られます</li>
          <li>フォームの入力欄は文字サイズが自動調整されるため、iOSの自動ズームは発生しません</li>
        </ul>

        <h4>画面の表示がおかしい・パネルがずれた</h4>
        <ul>
          <li>ブラウザの更新（F5キーまたは「⟳」ボタン）を試してください</li>
          <li>Manga モードと Work モードの切り替えを試してください</li>
          <li>別のブラウザで開いてみてください</li>
          <li>ダッシュボードのパネル配置がおかしい場合は、ページを再読み込みすると初期配置に戻ります</li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq',
    title: 'FAQ（よくある質問）',
    content: (
      <>
        <dl className="help-faq">
          <dt>Q. 勤務月と支払日は何が違いますか？</dt>
          <dd>
            <p><strong>勤務月</strong>は「その仕事が行われた月」（会計上の帰属月）です。<strong>支払日</strong>は「実際に振り込む日」です。</p>
            <p>例：5月分の作業に対して6月10日に支払う場合、勤務月は「2026-05」、支払日は「2026-06-10」になります。給与管理ページでは、デフォルトの支払日が勤務月の翌月10日に設定されています。</p>
          </dd>

          <dt>Q. 給与を「支払済み」にするのはいつですか？</dt>
          <dd>銀行振込が実際に完了したタイミングで「支払済み」に変更してください。振込CSVを出力しただけでは自動的に支払済みにはなりません。月次レポートを締める前に、支払状態が正しく反映されているか確認してください。</dd>

          <dt>Q. 月次締め後に修正できますか？</dt>
          <dd>
            <p>月次レポートページの「<strong>締めを解除</strong>」ボタンで解除理由を入力すると、再編集が可能になります。修正後に再度「<strong>承認・締め処理</strong>」を実行してください。</p>
            <p>なお、締め後でもアシスタントへの支払完了記録とCSV出力は引き続き行えます。</p>
          </dd>

          <dt>Q. アシスタントさんが日報を出していない場合はどこで見ますか？</dt>
          <dd>アシスタントページの「<strong>日報一覧</strong>」タブを開いてください。対象月・対象アシスタントで絞り込めます。提出がない場合は該当行にデータが表示されません。</dd>

          <dt>Q. シフト希望の未提出者はどこで確認しますか？</dt>
          <dd>アシスタントページの「<strong>シフト希望</strong>」タブに未提出者の催促バナーが表示されます。バナーから催促メールを送ることもできます（本メールが登録されているアシスタントのみ）。</dd>

          <dt>Q. 確定シフトを変更したら、誰に連絡すればよいですか？</dt>
          <dd>MangaOS では確定シフトを変更しても自動で通知は送信されません。変更後は手動でアシスタントさんへLINEやメールで連絡してください。なお確定シフトメールを再送することで最新の確定シフトを知らせることができます。</dd>

          <dt>Q. 仮メール（@mangaos.test）のアシスタントさんにはどう対応しますか？</dt>
          <dd>
            <p>仮メールのアシスタントには確定シフト通知やその他のシステムメールが届きません。アシスタントさんに本メールアドレスへの変更を依頼してください。</p>
            <p>変更方法：アシスタントさん自身がログイン後、右上のメールアドレスをクリック →「アカウント設定」→「メールアドレス変更」から変更できます。</p>
          </dd>

          <dt>Q. 固定費テンプレートと支出登録は何が違いますか？</dt>
          <dd>
            <p><strong>固定費テンプレート</strong>は毎月繰り返す支出を「型」として登録しておき、月初に一括生成するための機能です。家賃・国民健康保険・年金など定期的な支払いに使います。</p>
            <p><strong>支出登録</strong>は単発の支払いや、テンプレートに含まれない費用をその都度登録するための機能です。生成済みの固定費も「支払管理」の一覧から個別に編集できます。</p>
          </dd>

          <dt>Q. 事業用口座と漫画家用個人口座はどう使い分けますか？</dt>
          <dd>
            <p><strong>事業用口座</strong>（💼）は原稿料の入金や、アシスタント給与・必要経費などの事業的な支出に使います。</p>
            <p><strong>漫画家用個人口座</strong>（👤）は事業費に計上できない個人的な支払いや、口座間の振替などに使います。</p>
            <p>支出を登録する際に「支払口座」で選択してください。ダッシュボードではそれぞれの口座残高を確認できます。残高調整は「残高調整」ボタンから行えます。</p>
          </dd>

          <dt>Q. 作品・話数ページと日報ページの違いは何ですか？</dt>
          <dd>
            <p><strong>作品・話数ページ</strong>は「今どの話がどこまで進んでいるか（工程の進捗状況）」を管理する場所です。締切・工程ステータス・アーカイブなどを管理します。</p>
            <p><strong>日報ページ</strong>は「今日誰が何をしたか（実績の記録）」を管理する場所です。アシスタントさんの出勤実績・作業内容を日次で記録します。</p>
          </dd>

          <dt>Q. ダッシュボードの数字が合わないときは何を確認しますか？</dt>
          <dd>
            <ul>
              <li>月選択が意図した月になっているか確認してください</li>
              <li>口座残高がずれている場合は「残高調整」から手動で修正できます</li>
              <li>支払済み・入金済みの状態が正しく設定されているか、収入管理・支払管理で確認してください</li>
              <li>月次レポートが締め済みの月は変更できません。必要であれば「締めを解除」してから修正してください</li>
            </ul>
          </dd>

          <dt>Q. CSVをExcelで開くと文字化けします</dt>
          <dd>MangaOS のCSVファイルはUTF-8形式で出力されます。Excelで開く場合は「データ」タブ →「テキストまたはCSVから」でインポートし、文字コードとして「UTF-8」を選択してください。ファイルをダブルクリックで直接開くと文字化けする場合があります。</dd>
        </dl>
      </>
    ),
  },
]

// ----------------------------------------------------------------
// アドミンヘルプページ本体
// ----------------------------------------------------------------
export default function AdminHelp() {
  const [activeId, setActiveId] = useState<string>(sections[0].id)

  const activeSection = sections.find((s) => s.id === activeId) ?? sections[0]
  const activeIdx     = sections.findIndex((s) => s.id === activeId)

  return (
    <div className="help-layout">
      {/* 目次サイドバー */}
      <nav className="help-toc">
        <div className="help-toc-title">管理者ヘルプ</div>
        {sections.map((s) => (
          <button
            key={s.id}
            className={`help-toc-item${activeId === s.id ? ' active' : ''}`}
            onClick={() => setActiveId(s.id)}
          >
            {s.wip && <span className="help-toc-wip">🚧</span>}
            {s.title}
          </button>
        ))}
      </nav>

      {/* 本文 */}
      <article className="help-content">
        <div className="help-admin-badge">管理者向け</div>
        <h2 className="help-section-title">{activeSection.title}</h2>

        {activeSection.wip && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 600,
            background: 'var(--amber-light)',
            color: 'var(--amber)',
            border: '1px solid var(--amber-border, rgba(176,122,18,0.25))',
            borderRadius: 4,
            padding: '3px 8px',
            marginBottom: 16,
          }}>
            🚧 このセクションは準備中です
          </div>
        )}

        <div className="help-body">
          {activeSection.content}
        </div>

        {/* 前後ナビ */}
        <div className="help-nav-btns">
          {activeIdx > 0 && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setActiveId(sections[activeIdx - 1].id)}
            >
              ← 前へ
            </button>
          )}
          {activeIdx < sections.length - 1 && (
            <button
              className="btn btn-primary btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => setActiveId(sections[activeIdx + 1].id)}
            >
              次へ →
            </button>
          )}
        </div>
      </article>
    </div>
  )
}
